import { MOMENT_UNITS } from "../../../../global/utils/constants";

export default {
  name: "Wage Source Information",
  pageInfo: {
    text: `<b>IMPORTANT</b>: If you are requesting a wage source other than OEWS, select the requested non-OEWS wage source below. 
    You may only choose one of the listed wage sources at a time. When one is selected, the other non-OEWS wage 
    sources will be filled as "NO" and disabled from selection. 
    <b>Changing the visa classification type in Section A.1 will clear this entire section.</b>`,
    coloring: "grey",
  },
  prefix: "E",
  sections: [
    {
      fields: [
        {
          isGroup: {
            code: "E.1",
            machine_name: "coveredByAcwia",
            label:
              "Is the employer covered by ACWIA, as described in 20 CFR 656.40(e)(1)? * (Not applicable for H-2B)",
            tooltip: `Mark “Yes” if the employer is covered by the American Competitiveness and Workforce Improvement 
            Act (ACWIA) pursuant to 20 CFR 656.40(e) and “No” if it is not. Mark “N/A” only if the Application is for 
            an H-2B application.<br><b>Note:</b> ACWIA coverage does not apply to H-2B applications.<br>`,
            coloring: "none",
            type: "radio",
            fields: [
              {
                widget: "radio",
                choice: "Yes",
                value: 1,
                validate: ["required"],
                disabled: true,
                disableWhenField: [
                  {
                    when: "visaType",
                    is: "H-2B",
                  },
                  {
                    when: "sportsLeague",
                    is: 1,
                  },
                ],
                setSchema: [
                  {
                    set: "sportsLeague",
                    to: 0,
                  },
                  {
                    set: "pre_dbaSca",
                    to: 0,
                  },
                ],
                clearSchema: ["dbaSca"],
              },
              {
                widget: "radio",
                choice: "No",
                value: 0,
                validate: ["required"],
                disabled: true,
                disableWhenField: [
                  {
                    when: "visaType",
                    is: "H-2B",
                  },
                  {
                    when: "sportsLeague",
                    is: 1,
                  },
                ],
                clearSchema: [
                  "acwiaHigherEdu",
                  "acwiaAffiliated",
                  "acwiaNonprofit",
                  "coveredByAcwiaFiles",
                  "acwiaStatusChange",
                ],
                clearFiles: ["coveredByAcwiaFiles"],
              },
              {
                widget: "radio",
                choice: "N/A",
                value: 2,
                validate: ["required"],
                disabled: true,
                disableWhenField: {
                  when: "visaType",
                  is: [
                    "H-2B",
                    "H-1B",
                    "H-1B1 Chile",
                    "H-1B1 Singapore",
                    "E-3 Australian",
                    "PERM",
                  ],
                },
                clearSchema: [
                  "acwiaHigherEdu",
                  "acwiaAffiliated",
                  "acwiaNonprofit",
                  "coveredByAcwiaFiles",
                ],
                clearFiles: ["coveredByAcwiaFiles"],
              },
            ],
          },
        },
        {
          isGroup: {
            code: "E.1.a",
            label:
              "If “Yes,” identify which ACWIA provision the employer is covered under (choose all that apply): §",
            coloring: "none",
            disabled: true,
            machine_name: "acwiaHigherEdu", // need machine name here for group level error messaging
            tooltip: `If “Yes” in question 1, identify which ACWIA provision, as described in 20 CFR 656.40(e)(1), the employer is covered under (choose all that apply)</li>
              <ul>
                <li>Institution of higher education (20 CFR656.40(e)(1)(i));</li>
                <li>Affiliated or related nonprofit entity connected or associated with an institution of higher education (20 CFR 656.40(e)(1)(ii));</li>
                <li>Nonprofit research organization or Governmental research organization (20 CFR 656.40(e)(1)(iii))</li>
              </ul>`,
            fields: [
              {
                machine_name: "acwiaHigherEdu", // use validation check for group level validation
                widget: "checkbox",
                choice: "(i) Institution of higher education",
                label: "(i) Institution of higher education",
                hidden: true,
                disabled: true,
                disableWhenField: {
                  when: "visaType",
                  is: "H-2B",
                },
                validate: [
                  {
                    subsetOfSetRequired: "groupValidation",
                    message: "This field is required",
                    needsFieldValues: true,
                    extraData: {
                      when: "visaType",
                      is: "!=",
                      to: "H-2B",
                      numRequired: 1,
                      fieldArray: [
                        "acwiaHigherEdu",
                        "acwiaAffiliated",
                        "acwiaNonprofit",
                      ],
                    },
                  },
                ],
              },
              {
                machine_name: "acwiaAffiliated",
                widget: "checkbox",
                choice:
                  "(ii) Affiliated or related nonprofit entity connected or associated with an institution of higher education",
                label:
                  "(ii) Affiliated or related nonprofit entity connected or associated with an institution of higher education",
                hidden: true,
                disabled: true,
                disableWhenField: {
                  when: "visaType",
                  is: "H-2B",
                },
              },
              {
                machine_name: "acwiaNonprofit",
                widget: "checkbox",
                choice:
                  "(iii) Nonprofit research organization or Governmental research organization",
                label:
                  "(iii) Nonprofit research organization or Governmental research organization",
                hidden: true,
                disabled: true,
                disableWhenField: {
                  when: "visaType",
                  is: "H-2B",
                },
              },
            ],
            isVisible: {
              when: "coveredByAcwia",
              is: 1,
            },
          },
        },
        {
          isGroup: {
            code: "E.1.b",
            machine_name: "acwiaStatusChange",
            tooltip: `Identify whether the employer’s ACWIA status has changed since previously receiving a prevailing wage determination. If the employer was previously not ACWIA covered, mark “Yes” or “No” as to whether the employer has any reason to believe its ACWIA status has changed.  For example, mark “Yes” if an employer’s affiliation with an institution of higher education has changed.  If the answer is “Yes,” provide documentation.  Mark “N/A” if the employer has not previously received a prevailing wage determination.</li>`,
            label:
              "If the employer has previously been determined not covered under ACWIA, does the employer have any reason to believe that its status has changed? §",
            type: "radio",
            fields: [
              {
                widget: "radio",
                choice: "Yes",
                value: 1,
                validate: ["required"],
                disabled: true,
                disableWhenField: {
                  when: "visaType",
                  is: "H-2B",
                },
              },
              {
                widget: "radio",
                choice: "No",
                value: 0,
                validate: ["required"],
                disabled: true,
                disableWhenField: {
                  when: "visaType",
                  is: "H-2B",
                },
                clearSchema: ["coveredByAcwiaFiles"],
                clearFiles: ["coveredByAcwiaFiles"],
              },
              {
                widget: "radio",
                choice: "N/A",
                value: 2,
                validate: ["required"],
                disabled: true,
                disableWhenField: {
                  when: "visaType",
                  is: "H-2B",
                },
              },
            ],
            isVisible: {
              when: "coveredByAcwia",
              is: 1,
            },
          },
        },
        {
          isGroup: {
            code: "E.1.b",
            machine_name: "coveredByAcwiaFiles",
            label: `Document Upload - ACWIA Status Change`,
            // tooltip:
            //   "If “Agent” in question 1, mark “Yes” or “No” to indicate whether the employer is submitting a copy of the agent agreement or other documentation demonstrating the agent’s authority to represent the employer.  See 20 CFR 655.133(a).",
            coloring: "none",
            fields: [
              {
                label: `Uploaded ACWIA documentation.`,
                labelHidden: true,
                code: "E.1.b.1",
                machine_name: "coveredByAcwiaFiles",
                name: "ACWIA Document",
                widget: "file_upload",
                fileTypes: [".pdf", ".doc", ".docx", ".txt", ".xls", ".xlsx"],
                validate: ["required", "checkValue"],
                //validate: ['required'],
                description: `You may upload ACWIA documentation, but are not required to.`,
              },
            ],
            isVisible: {
              when: "acwiaStatusChange",
              is: 1,
            },
          },
        },
        {
          isGroup: {
            code: "E.2",
            machine_name: "sportsLeague",
            label:
              "Is the position covered by a Professional Sports League Rules or Regulations? §",
            tooltip: `Mark “Yes” if the job opportunity is for a professional athlete as defined in 8 U.S.C. 1182(a)(5)(A)(iii)(II) and covered by a professional sports league’s rules or regulations and mark “No” if it is not. If you mark “Yes,” a copy of the wage provisions from the professional sports league’s rules or regulations and relevant organization letters must be submitted with this application.  If the job opportunity is covered by a professional sports league’s rules or regulations, the employer may not request any other wage source.`,
            coloring: "none",
            type: "radio",
            fields: [
              {
                widget: "radio",
                choice: "Yes",
                value: 1,
                validate: ["required"],
                disabled: true,
                disableWhenField: [
                  {
                    when: "coveredByAcwia",
                    is: 1,
                  },
                  {
                    when: "cba",
                    is: 1,
                  },
                  {
                    when: "pre_dbaSca",
                    is: 1,
                  },
                  {
                    when: "dbaSurvey",
                    is: 1,
                  },
                ],
                setSchema: [
                  {
                    if: "visaType",
                    notEquals: "H-2B",
                    set: "coveredByAcwia",
                    to: 0,
                  },
                  {
                    set: "cba",
                    to: 0,
                  },
                  {
                    set: "pre_dbaSca",
                    to: 0,
                  },
                  {
                    set: "dbaSurvey",
                    to: 0,
                  },
                ],
              },
              {
                widget: "radio",
                choice: "No",
                value: 0,
                validate: ["required"],
                disabled: true,
                disableWhenField: [
                  {
                    when: "coveredByAcwia",
                    is: 1,
                  },
                  {
                    when: "cba",
                    is: 1,
                  },
                  {
                    when: "pre_dbaSca",
                    is: 1,
                  },
                  {
                    when: "dbaSurvey",
                    is: 1,
                  },
                ],
                clearFiles: [
                  "sportsLeagueOrganizationFiles",
                  "sportsLeagueLetterFiles",
                  "sportsLeagueUnionFiles",
                ],
                clearSchema: [
                  "sportsLeagueOrganizationFiles",
                  "sportsLeagueLetterFiles",
                  "sportsLeagueUnionFiles",
                ],
              },
            ],
          },
        },
        {
          isGroup: {
            code: "E.2",
            machine_name: "sportsLeagueOrganizationFiles",
            label: `You must upload the Sports League Employer Letter.`,
            // tooltip:
            //   "If “Agent” in question 1, mark “Yes” or “No” to indicate whether the employer is submitting a copy of the agent agreement or other documentation demonstrating the agent’s authority to represent the employer.  See 20 CFR 655.133(a).",
            coloring: "none",
            fields: [
              {
                machine_name: "sportsLeagueOrganizationFiles",
                name: "Sports League Employer Letter",
                code: "E.2.1",
                label: "Uploaded Sports League Employer Letter",
                labelHidden: true,
                widget: "file_upload",
                fileTypes: [".pdf", ".doc", ".docx", ".txt", ".xls", ".xlsx"],
                validate: ["required", "checkValue"],
                // description: `You may upload ACWIA documentation, but are not required to.`,
              },
            ],
            isVisible: {
              when: "sportsLeague",
              is: [[1]],
              operator: "compareMultiFieldValuesAND_ORInArray",
            },
          },
        },
        {
          isGroup: {
            code: "E.2",
            machine_name: "sportsLeagueLetterFiles",
            label: `You must upload the Sports League Union Letter/No Objection Letter.`,
            // tooltip:
            //   "If “Agent” in question 1, mark “Yes” or “No” to indicate whether the employer is submitting a copy of the agent agreement or other documentation demonstrating the agent’s authority to represent the employer.  See 20 CFR 655.133(a).",
            coloring: "none",
            fields: [
              {
                machine_name: "sportsLeagueLetterFiles",
                name: "Sports League Union Letter/No Objection Letter",
                code: "E.2.2",
                label:
                  "Uploaded Sports League Union Letter/No Objection Letter",
                labelHidden: true,
                widget: "file_upload",
                fileTypes: [".pdf", ".doc", ".docx", ".txt", ".xls", ".xlsx"],
                validate: ["required", "checkValue"],
                // description: `You may upload ACWIA documentation, but are not required to.`,
              },
            ],
            isVisible: {
              when: "sportsLeague",
              is: [[1]],
              operator: "compareMultiFieldValuesAND_ORInArray",
            },
          },
        },
        {
          isGroup: {
            code: "E.2",
            machine_name: "sportsLeagueUnionFiles",
            label: `You must upload the Sports League Rules.`,
            // tooltip:
            //   "If “Agent” in question 1, mark “Yes” or “No” to indicate whether the employer is submitting a copy of the agent agreement or other documentation demonstrating the agent’s authority to represent the employer.  See 20 CFR 655.133(a).",
            coloring: "none",
            fields: [
              {
                machine_name: "sportsLeagueUnionFiles",
                name: "Sports League Rules",
                code: "E.2.3",
                label: "Uploaded Sports League Rules",
                labelHidden: true,
                widget: "file_upload",
                fileTypes: [".pdf", ".doc", ".docx", ".txt", ".xls", ".xlsx"],
                validate: ["required", "checkValue"],
                // description: `You may upload ACWIA documentation, but are not required to.`,
              },
            ],
            isVisible: {
              when: "sportsLeague",
              is: [[1]],
              operator: "compareMultiFieldValuesAND_ORInArray",
            },
          },
        },
        {
          isGroup: {
            code: "E.3",
            machine_name: "cba",
            label:
              "Is the position covered by a Collective Bargaining Agreement (CBA)? §",
            tooltip: `Mark “Yes” if the position is covered by the wage provisions of a Collective Bargaining Agreement (CBA) and mark “No” if it is not. Mark “N/A” if you marked “Yes” in response to E.2.  If you mark “Yes,” a copy of the wage provisions from the CBA and relevant organization letters must be submitted with this application.`,
            coloring: "none",
            type: "radio",
            fields: [
              {
                widget: "radio",
                choice: "Yes",
                value: 1,
                validate: ["required"],
                disabled: true,
                disableWhenField: [
                  {
                    when: "sportsLeague",
                    is: 1,
                  },
                  {
                    when: "pre_dbaSca",
                    is: 1,
                  },
                  {
                    when: "dbaSurvey",
                    is: 1,
                  },
                ],
                clearSchema: [
                  "dbaSca",
                  "surveyName",
                  "surveyPublicationDate",
                  "surveyFiles",
                  "surveyFormEta9165Files",
                ],
                clearFiles: ["surveyFiles", "surveyFormEta9165Files"],
                setSchema: [
                  {
                    set: "pre_dbaSca",
                    to: 0,
                  },
                  {
                    set: "dbaSurvey",
                    to: 0,
                  },
                  {
                    set: "sportsLeague",
                    to: 0,
                  },
                ],
              },
              {
                widget: "radio",
                choice: "No",
                value: 0,
                validate: ["required"],
                clearSchema: [
                  "cbaFiles",
                  "cbaEmployerLetterFiles",
                  "cbaUnionLetterFiles",
                ],
                clearFiles: [
                  "cbaFiles",
                  "cbaEmployerLetterFiles",
                  "cbaUnionLetterFiles",
                ],
                disabled: true,
                disableWhenField: [
                  {
                    when: "sportsLeague",
                    is: 1,
                  },
                  {
                    when: "pre_dbaSca",
                    is: 1,
                  },
                  {
                    when: "dbaSurvey",
                    is: 1,
                  },
                ],
              },
              {
                widget: "radio",
                choice: "N/A",
                value: 2,
                validate: ["required"],
                clearSchema: [
                  "cbaFiles",
                  "cbaEmployerLetterFiles",
                  "cbaUnionLetterFiles",
                ],
                clearFiles: [
                  "cbaFiles",
                  "cbaEmployerLetterFiles",
                  "cbaUnionLetterFiles",
                ],
                disabled: true,
                disableWhenField: [
                  {
                    when: "sportsLeague",
                    is: 1,
                  },
                  {
                    when: "pre_dbaSca",
                    is: 1,
                  },
                  {
                    when: "dbaSurvey",
                    is: 1,
                  },
                ],
              },
            ],
          },
        },
        {
          isGroup: {
            code: "E.3",
            machine_name: "cbaFiles",
            label: `You must upload the relevant portions of your Collective Bargaining Agreement.`,
            // tooltip:
            //   "If “Agent” in question 1, mark “Yes” or “No” to indicate whether the employer is submitting a copy of the agent agreement or other documentation demonstrating the agent’s authority to represent the employer.  See 20 CFR 655.133(a).",
            coloring: "none",
            fields: [
              {
                machine_name: "cbaFiles",
                name: "Collective Bargaining Agreement",
                code: "E.3.1",
                label: "Uploaded Collective Bargaining Agreement",
                labelHidden: true,
                widget: "file_upload",
                fileTypes: [".pdf", ".doc", ".docx", ".txt", ".xls", ".xlsx"],
                validate: ["required", "checkValue"],
                // description: `You may upload ACWIA documentation, but are not required to.`,
              },
            ],
            isVisible: {
              when: "cba",
              is: 1,
            },
          },
        },
        {
          isGroup: {
            code: "E.3",
            machine_name: "cbaEmployerLetterFiles",
            label: `You must upload the CBA Employer Letter.`,
            // tooltip:
            //   "If “Agent” in question 1, mark “Yes” or “No” to indicate whether the employer is submitting a copy of the agent agreement or other documentation demonstrating the agent’s authority to represent the employer.  See 20 CFR 655.133(a).",
            coloring: "none",
            fields: [
              {
                machine_name: "cbaEmployerLetterFiles",
                name: "CBA Employer Letter",
                code: "E.3.2",
                label: "Uploaded CBA Employer Letter",
                labelHidden: true,
                widget: "file_upload",
                fileTypes: [".pdf", ".doc", ".docx", ".txt", ".xls", ".xlsx"],
                validate: ["required", "checkValue"],
                // description: `You may upload ACWIA documentation, but are not required to.`,
              },
            ],
            isVisible: {
              when: "cba",
              is: 1,
            },
          },
        },
        {
          isGroup: {
            code: "E.3",
            machine_name: "cbaUnionLetterFiles",
            label: `You must upload the CBA Union Letter.`,
            // tooltip:
            //   "If “Agent” in question 1, mark “Yes” or “No” to indicate whether the employer is submitting a copy of the agent agreement or other documentation demonstrating the agent’s authority to represent the employer.  See 20 CFR 655.133(a).",
            coloring: "none",
            fields: [
              {
                machine_name: "cbaUnionLetterFiles",
                name: "CBA Union Letter",
                code: "E.3.3",
                label: "Uploaded CBA Union Letter",
                labelHidden: true,
                widget: "file_upload",
                fileTypes: [".pdf", ".doc", ".docx", ".txt", ".xls", ".xlsx"],
                validate: ["required", "checkValue"],
                // description: `You may upload ACWIA documentation, but are not required to.`,
              },
            ],
            isVisible: {
              when: "cba",
              is: 1,
            },
          },
        },
      ],
    },
    {
      fields: [
        {
          isGroup: {
            code: "E.4",
            machine_name: "pre_dbaSca",
            label:
              "Is the employer requesting a prevailing wage based on the Davis-Bacon Act (DBA) or McNamara Service Contract Act (SCA) (Not applicable for H-2B)?",
            tooltip: `
                Mark "Yes" if the employer is requesting a prevailing wage determination based on the Davis-Bacon Act (DBA) or the McNamara-O'Hara Service Contract Act (SCA). <br/> <br/> Note: SCA and DBA wages do not apply to H-2B applications.  
                `,
            coloring: "none",
            type: "radio",
            required: true,
            fields: [
              {
                widget: "radio",
                choice: "Yes",
                value: 1,
                validate: ["required"],
                disabled: true,
                disableWhenField: [
                  {
                    when: "coveredByAcwia",
                    is: 1,
                  },
                  {
                    when: "sportsLeague",
                    is: 1,
                  },
                  {
                    when: "cba",
                    is: 1,
                  },
                  {
                    when: "dbaSurvey",
                    is: 1,
                  },
                  {
                    when: "coveredByAcwia",
                    is: "2",
                  },
                  {
                    when: "visaType",
                    is: "H-2B",
                  },
                ],
                setSchema: [
                  {
                    set: "cba",
                    to: 0,
                  },
                  {
                    set: "dbaSurvey",
                    to: 0,
                  },
                  {
                    set: "sportsLeague",
                    to: 0,
                  },
                ],
              },
              {
                widget: "radio",
                choice: "No",
                value: 0,
                validate: ["required"],
                clearSchema: ["dbaSca"],
                disabled: true,
                disableWhenField: [
                  {
                    when: "coveredByAcwia",
                    is: 1,
                  },
                  {
                    when: "sportsLeague",
                    is: 1,
                  },
                  {
                    when: "cba",
                    is: 1,
                  },
                  {
                    when: "dbaSurvey",
                    is: 1,
                  },
                  {
                    when: "coveredByAcwia",
                    is: 2,
                  },
                  {
                    when: "visaType",
                    is: "H-2B",
                  },
                ],
              },
            ],
          },
        },
        {
          isGroup: {
            code: "E.4.a",
            machine_name: "dbaSca",
            label:
              "Because you said yes to E.4, identify which wage source the employer is requesting. §",
            tooltip:
              'If "Yes" in question 4, identify if the employer requests use of a DBA prevailing wage determination or SCA prevailing wage determination. For DBA, indicate in the job duties (Section F.a.2) which one of the four types of construction applies: building, residential, highway, or heavy.',
            coloring: "none",
            isVisible: {
              when: "pre_dbaSca",
              is: 1,
            },
            fields: [
              {
                widget: "radio",
                choice: "DBA",
                value: "DBA",
                validate: ["required"],
                // validate: [
                //   {
                //     isNotRequired: "sportsLeague",
                //     //message: "This field is required",
                //     needsFieldValues: false,
                //     extraData: {
                //       valueOf: 2,
                //     },
                //   },
                // ],
                // disabled: true,
                // disableWhenField: [
                //   {
                //     when: "visaType",
                //     is: "H-2B",
                //   },
                //   {
                //     when: "sportsLeague",
                //     is: 1,
                //   },
                //   {
                //     when: "cba",
                //     is: 1,
                //   },
                // ],
              },
              {
                widget: "radio",
                choice: "SCA",
                value: "SCA",
                validate: ["required"],
                // validate: [
                //   {
                //     isNotRequired: "sportsLeague",
                //     //message: "This field is required",
                //     needsFieldValues: false,
                //     extraData: {
                //       valueOf: 2,
                //     },
                //   },
                // ],
                // disabled: true,
                // disableWhenField: [
                //   {
                //     when: "visaType",
                //     is: "H-2B",
                //   },
                //   {
                //     when: "sportsLeague",
                //     is: 1,
                //   },
                //   {
                //     when: "cba",
                //     is: 1,
                //   },
                // ],
              },
            ],
          },
        },
        {
          isGroup: {
            code: "E.5",
            machine_name: "dbaSurvey",
            label:
              "Is the employer requesting consideration of a survey as a wage source in determining the prevailing wage?",
            tooltip: `Mark "Yes" if the employer is requesting consideration of an employer-provided survey as the wage source in determining the prevailing wage`,
            coloring: "none",
            type: "radio",
            required: true,
            fields: [
              {
                widget: "radio",
                choice: "Yes",
                value: 1,
                validate: ["required"],
                disabled: true,
                disableWhenField: [
                  {
                    when: "sportsLeague",
                    is: 1,
                  },
                  {
                    when: "cba",
                    is: 1,
                  },
                  {
                    when: "pre_dbaSca",
                    is: 1,
                  },
                ],
                setSchema: [
                  {
                    set: "cba",
                    to: 0,
                  },
                  {
                    set: "pre_dbaSca",
                    to: 0,
                  },
                  {
                    set: "sportsLeague",
                    to: 0,
                  },
                ],
              },
              {
                widget: "radio",
                choice: "No",
                value: 0,
                validate: ["required"],
                clearSchema: [
                  "surveyName",
                  "surveyPublicationDate",
                  "surveyFiles",
                  "surveyFormEta9165Files",
                ],
                clearFiles: ["surveyFiles", "surveyFormEta9165Files"],
                disabled: true,
                disableWhenField: [
                  {
                    when: "sportsLeague",
                    is: 1,
                  },
                  {
                    when: "cba",
                    is: 1,
                  },
                  {
                    when: "pre_dbaSca",
                    is: 1,
                  },
                ],
              },
            ],
          },
        },
        {
          code: "E.5.a",
          machine_name: "surveyName",
          widget: "text",
          label: "Survey name or title:",
          tooltip:
            'If "Yes" in question 5, provide the name or title of the survey under which the employer is requesting a determination.',
          validate: ["required"],
          maxLength: 60,
          isVisible: {
            when: "dbaSurvey",
            is: 1,
          },
        },
        {
          code: "E.5.b",
          machine_name: "surveyPublicationDate",
          widget: "date",
          label:
            "Survey date of publication or, if not published, date of submission to DOL:",
          tooltip:
            'If "Yes" in question 5, provide the date of publication of the survey under which the employer is requesting a determination. For unpublished surveys, such as ad-hoc or one-time surveys, use the date the Form ETA-9141 is being submitted.',
          validate: [
            "required",
            "noFutureDate",
            {
              dateIsBefore: "surveyPublicationDate",
              message: "Please pick a date within the last 24 months",
              needsFieldValues: true,
              extraData: {
                amount: 24,
                unit: MOMENT_UNITS.MONTHS,
              },
            },
          ],
          isVisible: {
            when: "dbaSurvey",
            is: 1,
          },
        },
        {
          isGroup: {
            code: "",
            machine_name: "surveyFiles",
            label: `You must upload the survey.`,
            // tooltip:
            //   "If “Agent” in question 1, mark “Yes” or “No” to indicate whether the employer is submitting a copy of the agent agreement or other documentation demonstrating the agent’s authority to represent the employer.  See 20 CFR 655.133(a).",
            coloring: "none",
            fields: [
              {
                machine_name: "surveyFiles",
                name: "Employer-Provided Survey",
                label: "Uploaded Employer-Provided Survey",
                labelHidden: true,
                widget: "file_upload",
                fileTypes: [".pdf", ".doc", ".docx", ".txt", ".xls", ".xlsx"],
                validate: ["required", "checkValue"],
              },
            ],
            isVisible: {
              when: "dbaSurvey",
              is: 1,
            },
          },
        },
        {
          isGroup: {
            code: "",
            machine_name: "surveyFormEta9165Files",
            label: `
                  Document Upload - Survey Attestations (H-2B Prevailing Wage Determination Request)
                  <br>
                  <small>
                    Step 1: Download <a rel="noreferrer" target='_blank' href='https://www.dol.gov/sites/dolgov/files/ETA/oflc/pdfs/Form-ETA-9165_508-Compliant-09.14.2023-v1.pdf'>Form ETA-9165</a> PDF<br>
                    Step 2: Complete<br>
                    Step 3: Upload below
                  </small>
                  <br>`,
            // tooltip:
            //   "If “Agent” in question 1, mark “Yes” or “No” to indicate whether the employer is submitting a copy of the agent agreement or other documentation demonstrating the agent’s authority to represent the employer.  See 20 CFR 655.133(a).",
            coloring: "none",
            fields: [
              {
                machine_name: "surveyFormEta9165Files",
                name: "Form ETA-9165",
                label: "Uploaded Form ETA-9165",
                labelHidden: true,
                widget: "file_upload",
                fileTypes: [".pdf", ".doc", ".docx", ".txt", ".xls", ".xlsx"],
                validate: ["required", "checkValue"],
                // description: `You may upload ACWIA documentation, but are not required to.`,
              },
            ],
            isVisible: {
              when: "dbaSurvey visaType",
              is: [[1], ["H-2B"]],
              operator: "compareMultiFieldValuesAND_ORInArray",
            },
          },
        },
      ],
    },
  ],
};
